import { useState, type ChangeEvent } from 'react';
import { planErrorMessage } from '../../components/plan/planErrors';
import { usePlanName } from '../../components/plan/usePlan';
import { TicketHead } from '../../components/TicketHead';
import {
  IMAGE_ACCEPT_ATTRIBUTE,
  assetFileName,
  uploadImageToAssets,
  validateImageFile,
} from '../../lib/storage';
import { AssetPreview } from '../knowledge-base/AssetPreview';
import { assetsApi } from '../knowledge-base/knowledgeBaseApi';
import { useCollection } from '../knowledge-base/useCollection';
import type { AssetRead } from '../../types/api';

interface ImageAssetPickerProps {
  businessId: string;
  /** Currently chosen asset id, or null when nothing is picked yet. */
  selectedId: string | null;
  /** Called with null when the chosen asset is removed out from under the form. */
  onSelect: (assetId: string | null) => void;
  disabled?: boolean;
}

/**
 * Picks the image a single-image post sits on, when the brief said the user is
 * bringing one. A brief that asked us to generate the picture never renders this
 * — it sends `image_asset_id: null` and gets a rendered square instead.
 *
 * Reads the same `assets` collection the knowledge base manages and uploads
 * through the same direct-to-Storage path, so there is one upload rule set and
 * one signed-preview cache in the app. A file dropped here is a real asset row
 * afterwards — it shows up in the knowledge base like any other upload.
 */
export function ImageAssetPicker({
  businessId,
  selectedId,
  onSelect,
  disabled = false,
}: ImageAssetPickerProps) {
  const planName = usePlanName();
  const collection = useCollection(assetsApi, businessId);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function handleUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!file) return;

    const rejected = validateImageFile(file);
    if (rejected) {
      setUploadError(rejected);
      return;
    }

    setUploadError(null);
    setIsUploading(true);
    try {
      const storagePath = await uploadImageToAssets(businessId, file);
      const created = await collection.create({
        storage_path: storagePath,
        kind: 'product_photo',
        tags: [],
        product_id: null,
      });
      // Just-uploaded is almost always the intended pick.
      onSelect(created.id);
    } catch (caught) {
      setUploadError(planErrorMessage(caught, 'Upload failed. Try again.', { plan: planName }));
    } finally {
      setIsUploading(false);
    }
  }

  /** Optimistic: the card goes now, the DELETE waits out the undo window. */
  function startDelete(item: AssetRead) {
    if (item.id === selectedId) onSelect(null);
    collection.removeWithUndo({
      id: item.id,
      label: assetFileName(item.storage_path),
      failureMessage: 'That image could not be removed.',
    });
  }

  const isBusy = disabled || isUploading;
  const error = uploadError ?? collection.error ?? collection.deleteError;

  return (
    <fieldset className="asset-picker" disabled={disabled}>
      <legend className="field__label">Image</legend>
      <p className="field__hint">
        This post goes out on your own picture — nothing is rendered for it. Pick one already on
        file, or add a new one.
      </p>

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      {collection.isLoading ? (
        <p className="kb-section__status" role="status">
          Loading your images
        </p>
      ) : collection.items.length === 0 ? (
        <div className="kb-section__empty">
          <p className="kb-section__empty-text">
            No images added yet. Upload the picture this post goes out on. PNG, JPEG, WebP or GIF,
            under 5 MB.
          </p>
        </div>
      ) : (
        <ul className="asset-picker__grid">
          {collection.items.map((item) => {
            const isSelected = item.id === selectedId;
            return (
              <li
                className={isSelected ? 'asset-option asset-option--selected' : 'asset-option'}
                key={item.id}
              >
                <label className="asset-option__label">
                  <input
                    className="visually-hidden"
                    type="radio"
                    name="generation-image"
                    value={item.id}
                    checked={isSelected}
                    disabled={isBusy}
                    onChange={() => onSelect(item.id)}
                  />
                  <TicketHead label="Image" timestamp={item.created_at} />
                  <AssetPreview storagePath={item.storage_path} />
                  <span className="asset-option__name">{assetFileName(item.storage_path)}</span>
                </label>
                <button
                  className="button button--ghost button--danger-text asset-option__remove"
                  type="button"
                  disabled={isBusy}
                  onClick={() => startDelete(item)}
                >
                  Remove
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="asset-picker__actions">
        <label className="button" aria-disabled={isBusy}>
          {isUploading ? 'Uploading' : 'Upload an image'}
          <input
            className="visually-hidden"
            type="file"
            accept={IMAGE_ACCEPT_ATTRIBUTE}
            disabled={isBusy}
            onChange={(event) => void handleUpload(event)}
          />
        </label>
      </div>
    </fieldset>
  );
}
